import { queryOptions, useMutation } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import {
  cropUnitSchema,
  traderSchema,
} from "@/api/types/digitalnisklady.cz/types";
import { entitySchema } from "@/common/schema/entity";
import { graphql } from "@/generated/digitalnisklady.cz";
import {
  AmountDiscountType,
  AmountDiscountValueType,
  CropCategory,
  Currency,
} from "@/generated/digitalnisklady.cz/graphql";

// eslint-disable-next-line lingui/no-unlocalized-strings
const addFastContractDocument = graphql(`
  mutation AddFastMaterialContract($fastContract: FastContractInput!) {
    addFastContract(fastContract: $fastContract) {
      id
    }
  }
`);

type FastMaterialContractPayload = {
  amount: number;
  cropId: string;
  marketPriceId: string;
  gps: {
    latitude: number;
    longitude: number;
  };
  companyId: string;
  currency: Currency;
  note?: string;
  zip: string;
};

const useAddMaterialContractMutation = () =>
  useMutation({
    mutationFn: (payload: FastMaterialContractPayload) =>
      graphqlClient.request(addFastContractDocument, {
        fastContract: {
          amount: payload.amount,
          company: payload.companyId,
          crop: payload.cropId,
          currency: payload.currency,
          gps: payload.gps,
          marketPrice: payload.marketPriceId,
          note: payload.note,
          zip: payload.zip,
        },
      }),
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const marketplaceItemDocument = graphql(`
  query MarketplaceItem($id: ID!) {
    crop(id: $id) {
      id
      name
      category
      cropTraders {
        edges {
          node {
            trader {
              id
              logo
              name
              tocUrl
            }
            minimumAmount
          }
        }
      }
    }
  }
`);

const marketplaceItemSchema = entitySchema.extend(
  z.object({
    category: z.enum(CropCategory),
    traders: z.array(traderSchema),
  }).shape,
);

const marketplaceItemQuery = (id: string) =>
  queryOptions({
    queryFn: () => graphqlClient.request(marketplaceItemDocument, { id }),
    queryKey: ["marketplaceItem", id],
    select: (data) =>
      marketplaceItemSchema.parse({
        category: data.crop?.category,
        id: data.crop?.id,
        name: data.crop?.name,
        traders: data.crop?.cropTraders?.edges?.map((node) => ({
          id: node?.node?.trader?.id,
          logo: node?.node?.trader?.logo,
          name: node?.node?.trader?.name,
          tocUrl: node?.node?.trader?.tocUrl,
        })),
      }),
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const marketplaceItemPricesHistoryDocument = graphql(`
  query MarketplaceItemPricesHistory(
    $aggregated: Boolean
    $cropId: [ID!]
    $currency: Currency!
    $from: DateTime!
    $latitude: Float!
    $longitude: Float!
    $quarterId: ID
    $to: DateTime
    $traderIds: [ID!]
    $companyId: ID
  ) {
    getMarketPrices(
      cropIds: $cropId
      aggregated: $aggregated
      from: $from
      quarterId: $quarterId
      to: $to
      traderIds: $traderIds
      gps: { latitude: $latitude, longitude: $longitude }
      companyId: $companyId
    ) {
      date
      price(currency: $currency)
    }
  }
`);

const marketplaceItemPricesHistorySchema = z.array(
  z.object({
    datetime: z.string(),
    rate: z.number(),
  }),
);

const marketplaceItemPriceHistoryQuery = (variables: {
  cropId: string;
  currency: Currency;
  from: string;
  to: string;
  latitude: number;
  longitude: number;
  aggregated: boolean;
  traderIds: string[];
  quarterId: string;
  companyId: string;
}) =>
  queryOptions({
    queryFn: async () => {
      const prices = await graphqlClient.request(
        marketplaceItemPricesHistoryDocument,
        variables,
      );

      return prices;
    },
    queryKey: ["marketplace-item-prices-history", variables],
    select: (data) => {
      const result = data.getMarketPrices?.map((edge) => ({
        datetime: DateTime.fromSeconds(edge?.date).toLocaleString({
          day: "numeric",
          month: "numeric",
          year: "2-digit",
        }),
        rate: edge?.price,
      }));
      return marketplaceItemPricesHistorySchema.parse(result);
    },
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const marketplaceItemTraderPricesDocument = graphql(`
  query TraderPrices(
    $latitude: Float!
    $longitude: Float!
    $cropId: ID
    $currency: Currency!
    $quarterId: ID
    $locale: String
  ) {
    cropPricesInRegion(
      gps: { latitude: $latitude, longitude: $longitude }
      cropId: $cropId
      tradeType: TO_STORAGE
      quarterId: $quarterId
    ) {
      id
      crop {
        id
        name(locale: $locale)
        unit
        cropTraders {
          edges {
            node {
              trader {
                id
                logo
                name
                tocUrl
              }
              description
              urlInfo
              image
              minimumAmount
              timeFromTrading
              timeToTrading
            }
          }
        }
        amountDiscounts {
          edges {
            node {
              id
              fromAmount
              toAmount
              value
              valueType
              type
              currency
              trader {
                id
              }
            }
          }
        }
      }
      quarter {
        id
        name
      }
      price(currency: $currency)
      trader {
        id
        logo
        name
        tocUrl
      }
    }
  }
`);

const amountDiscountSchema = z.object({
  currency: z.enum(Currency).or(z.null()),
  fromAmount: z.number(),
  id: z.string(),
  toAmount: z.number(),
  type: z.enum(AmountDiscountType),
  value: z.number(),
  valueType: z.enum(AmountDiscountValueType),
});

const marketplaceItemTraderPricesSchema = z.array(
  z.object({
    id: z.string(),
    price: z.number(),
    quarter: entitySchema,
    trader: traderSchema.extend(
      z.object({
        description: z.string().optional(),
        discounts: z.array(amountDiscountSchema).default([]),
        image: z.string().optional().nullable(),
        minimumAmount: z.number().optional().nullable(),
        unit: cropUnitSchema,
        urlInfo: z.string().optional().nullable(),
      }).shape,
    ),
  }),
);

const marketplaceItemTraderPricesQuery = (variables: {
  cropId: string;
  quarterId: string;
  currency: Currency;
  latitude: number;
  longitude: number;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(marketplaceItemTraderPricesDocument, variables),
    queryKey: ["marketplace-item-trader-prices", variables],
    select: (data) => {
      const result = data.cropPricesInRegion?.map((edge) => {
        const match = edge?.crop?.cropTraders?.edges?.find(
          (trader) => trader?.node?.trader?.id === edge.trader?.id,
        );
        const traderDiscounts = (edge?.crop?.amountDiscounts?.edges || [])
          .filter((discount) => discount?.node?.trader.id === edge?.trader?.id)
          .map((discount) => discount?.node)
          .filter((node): node is NonNullable<typeof node> => node !== null)
          .toSorted((a, b) => (a.fromAmount ?? 0) - (b.fromAmount ?? 0));
        return {
          id: edge?.id,
          price: edge?.price,
          quarter: edge?.quarter,
          trader: {
            ...edge?.trader,
            description: match?.node?.description,
            discounts: traderDiscounts,
            image: match?.node?.image,
            logo: match?.node?.trader?.logo,
            minimumAmount: match?.node?.minimumAmount,
            timeFromTrading: match?.node?.timeFromTrading,
            timeToTrading: match?.node?.timeToTrading,
            unit: edge?.crop?.unit,
            urlInfo: match?.node?.urlInfo,
          },
        };
      });

      return marketplaceItemTraderPricesSchema.parse(
        result?.toSorted((a, b) => a.price! - b.price!),
      );
    },
  });
// eslint-disable-next-line lingui/no-unlocalized-strings
const getMarketPriceDocument = graphql(`
  query GetMarketPrice($id: ID!, $amount: Int, $currency: Currency!) {
    getMarketPrice(id: $id) {
      id
      price(amount: $amount, currency: $currency)
    }
  }
`);

const marketPriceQuery = (variables: {
  id: string;
  amount?: number;
  currency: Currency;
}) =>
  queryOptions({
    enabled: variables.amount !== undefined && variables.amount > 0,
    queryFn: async () => {
      const result = await graphqlClient.request(getMarketPriceDocument, {
        amount: variables.amount,
        currency: variables.currency,
        id: variables.id,
      });
      return result;
    },
    queryKey: ["getMarketPrice", variables],
    select: (data) => data.getMarketPrice?.price ?? null,
  });

export {
  marketplaceItemPriceHistoryQuery,
  marketplaceItemQuery,
  marketplaceItemTraderPricesQuery,
  marketPriceQuery,
  useAddMaterialContractMutation,
};
